import React from 'react';
import type { Aktivitet } from '@klp/formgenerator/interfaces';
import { Block } from '@klp/design/Block';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Divider,
} from '@material-ui/core';
import { ExpandMore } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { Typography } from '@klp/design/Typography';
import { CamundaFields } from '@forvalter/utils/CamundaUtils';
import { useTildelKommentar } from './hooks/useTildelKommentar';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import {
    formatDateWithTime,
    parseSafeDate,
} from '@klp/utils/dateTimeFormatter';

const styles = () => ({
    comment: {
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
    },
});

export type TildelKommentarListProps = {
    currentActivity: Aktivitet;
    defaultExpanded?: boolean;
};

export const TildelKommentarList: React.FC<TildelKommentarListProps> = ({
    currentActivity,
    defaultExpanded = false,
}) => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, styles);
    const { allComments } = useTildelKommentar(currentActivity);

    const [accordionExpanded, setAccordionExpanded] =
        React.useState(defaultExpanded);

    const handleAccordionChange = () => {
        setAccordionExpanded(!accordionExpanded);
    };

    const numberOfComments = allComments.length;

    return (
        <Accordion
            data-testid="ProsjektTildelKommentarerAccordion"
            expanded={accordionExpanded}
            onChange={handleAccordionChange}
        >
            <AccordionSummary expandIcon={<ExpandMore />}>
                {t(`prosjekt.${CamundaFields.TILDEL_KOMMENTARER}.list.title`)}
            </AccordionSummary>
            <AccordionDetails>
                <Block>
                    {numberOfComments > 0 &&
                        allComments.map((comment, index) => (
                            <Block
                                key={`${comment.activityId}-${comment.timestamp}-${index}`}
                                marginBottom="compact"
                            >
                                <Typography variant="body2">
                                    <strong>{comment.user}</strong>
                                </Typography>
                                <Typography variant="caption">
                                    {formatDateWithTime(
                                        parseSafeDate(comment.timestamp),
                                    )}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    className={classes.comment}
                                >
                                    {comment.comment}
                                </Typography>
                                {numberOfComments !== index + 1 && (
                                    <Block marginTop="tiny">
                                        <Divider />
                                    </Block>
                                )}
                            </Block>
                        ))}
                    {numberOfComments === 0 && (
                        <Typography variant="body2">
                            {t(
                                `prosjekt.${CamundaFields.TILDEL_KOMMENTARER}.list.empty`,
                            )}
                        </Typography>
                    )}
                </Block>
            </AccordionDetails>
        </Accordion>
    );
};
